import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Select, Table } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../components/PersianDatePicker';
import ReportPageLayout from './ReportPageLayout';
import ReportFiltersCard from './ReportFiltersCard';
import { reportsApi } from '../../utils/api';
import { getLast30DaysRange } from '../../utils/dateUtils';
import { extractPagination, extractRows, getDateRangePickerValue, parseDateRangeChange } from './reportUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatReportTableAmount } from './chartFormatters';

const SalesReport = () => {
  const range = getLast30DaysRange('jYYYY-MM-DD', 'jalali');
  const [filters, setFilters] = useState({ startDate: range.startDate, endDate: range.endDate, client_id: 'all', page: 1, per_page: 15 });
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [clients, setClients] = useState([]);

  const load = async (next) => {
    if (!next.startDate || !next.endDate) return;
    setLoading(true);
    try {
      const res = await reportsApi.getSalesReport(next);
      setRows(extractRows(res));
      setPagination(extractPagination(res));
      setClients(res?.filters?.clients || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => load(filters), 350);
    return () => clearTimeout(timer);
  }, [filters]);

  return (
    <ReportPageLayout
      headerIcon={<FileTextOutlined />}
      title="گزارش فروش"
      subtitle="لیست فاکتورهای فروش "
      onExport={async () => {
        setExportLoading(true);
        try {
          await reportsApi.exportSalesReport(filters);
        } finally {
          setExportLoading(false);
        }
      }}
      exportLoading={exportLoading}
    >
      <ReportFiltersCard>
        <Form layout="vertical" className="custom-font">
          <Row gutter={[16, 12]}>
            <Col xs={24} lg={12}>
              <Form.Item label="بازه تاریخ" className="mb-0">
                <PersianDatePicker
                  range
                  placeholder="انتخاب بازه از تاریخ تا تاریخ"
                  value={getDateRangePickerValue(filters.startDate, filters.endDate)}
                  onChange={(dates) => setFilters((p) => ({ ...p, ...parseDateRangeChange(dates), page: 1 }))}
                  style={{ minHeight: 36 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="مشتری" className="mb-0">
                <Select
                  value={filters.client_id}
                  onChange={(v) => setFilters((p) => ({ ...p, client_id: v, page: 1 }))}
                  options={[{ value: 'all', label: 'همه' }, ...clients.map((i) => ({ value: i.id, label: i.name }))]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </ReportFiltersCard>

      {loading && !rows.length ? (
        <TableSkeleton rows={6} columns={8} />
      ) : (
        <Table
          rowKey={(r) => r.id}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1200 }}
          className="custom-font"
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => {
              const next = { ...filters, page, per_page: pageSize };
              setFilters(next);
            },
          }}
          columns={[
            { title: 'تاریخ پرداخت', dataIndex: 'paid_on' },
            { title: 'شماره فاکتور', dataIndex: 'invoice_number' },
            { title: 'مشتری', dataIndex: 'client_name' },
            { title: 'ارزش فاکتور', dataIndex: 'invoice_value', render: formatReportTableAmount },
            { title: 'ارزش مشمول مالیات', dataIndex: 'taxable_value', render: formatReportTableAmount },
            { title: 'تخفیف', dataIndex: 'discount', render: formatReportTableAmount },
            { title: 'مبلغ پرداختی', dataIndex: 'amount_paid', render: formatReportTableAmount },
            { title: 'حساب بانکی', dataIndex: 'bank_account' },
          ]}
        />
      )}
    </ReportPageLayout>
  );
};

export default SalesReport;
